import React from 'react';
import { pickDaily, ROTATION_SALTS } from '../utils/dailyRotation';

export interface HeroMoment {
  /** Headline lead-in, e.g. "The girls are" */
  lead: string;
  /** Highlighted closing word/phrase, rendered with the yellow marker style */
  mark: string;
  dek: React.ReactNode;
}

/**
 * Rotating hero copy so the splash doesn't go stale. One picked per day
 * (see pickHeroMoment) rather than a true auto-advancing carousel — a
 * single headline most visitors see once doesn't need motion, just variety.
 */
export const heroMoments: HeroMoment[] = [
  {
    lead: 'No locks,',
    mark: 'just receipts.',
    dek: (
      <>
        We publish the pick, explain the case, and grade it after the final. <b>NBA</b>,{' '}
        <b>MLB</b>, <b>NHL</b> and <b>NFL</b>, with enough context for the group chat and enough
        honesty for the morning after.
      </>
    ),
  },
  {
    lead: 'Read the line',
    mark: 'first.',
    dek: (
      <>
        Odds move because money, injuries, travel, and public panic all leave fingerprints.
        We sort the <b>NBA</b>, <b>MLB</b>, <b>NHL</b> and <b>NFL</b> slate into picks you can
        follow, question, or fade.
      </>
    ),
  },
  {
    lead: 'Good picks need',
    mark: 'paper trails.',
    dek: (
      <>
        Anyone can love a side before tip. We keep the final score attached. Straight up,
        against the spread, totals, parlays when they earn it: every published pick comes back
        to the ledger.
      </>
    ),
  },
  {
    lead: 'The board has to',
    mark: 'make sense.',
    dek: (
      <>
        A lean is not enough. We look for the price, the matchup, the schedule spot, and the
        reason not to bet it. If the case survives that, it can go on the slip.
      </>
    ),
  },
  {
    lead: 'We like the play,',
    mark: 'then grade it.',
    dek: (
      <>
        The fun part is arguing the side. The useful part is checking whether it actually won.
        We keep both: readable picks before the game, public results after it.
      </>
    ),
  },
  {
    lead: 'Your favorite team',
    mark: 'is not a plan.',
    dek: (
      <>
        Loyalty bets are fun until the number asks follow-up questions. We make the case for
        the side, the total, or the pass, then keep the receipt where everyone can see it.
      </>
    ),
  },
  {
    lead: 'The number moved,',
    mark: 'so did we.',
    dek: (
      <>
        Lines shift. Starters sit. A total that made sense at lunch can look suspicious by
        dinner. We track the slate like it is alive, because it usually is.
      </>
    ),
  },
  {
    lead: 'Straight answers for',
    mark: 'messy slates.',
    dek: (
      <>
        Busy night? Start with what matters: which games are worth watching, which lines are
        worth questioning, and which picks we are willing to see again on the record page.
      </>
    ),
  },
];

/**
 * Deterministic per day (not per render) so the hero doesn't flicker between
 * variants on re-render, and so tests can predict the pick from a fixed date.
 * Seeded by the full local date, so the rotation differs week to week instead
 * of pinning each variant to a weekday.
 */
export function pickHeroMoment(date: Date = new Date()): HeroMoment {
  return pickDaily(heroMoments, date, ROTATION_SALTS.hero) as HeroMoment;
}
